import React, { useMemo, useState, useCallback, useRef, useEffect, RefObject } from 'react';
import { useParams, useLocation } from 'react-router';
import { debounce } from 'lodash';
import { t } from '@sm/intl';
import { Box } from '@wds/box';
import { Accordion } from '@wds/accordion';
import { IconCaretUp, IconCaretDown } from '@wds/icons';
import { Button } from '@sm/wds-react';
import getFeatureCategories from '~pricing/transformers/details';
import FeatureTooltip from '~pricing/components/Tooltip';
import categoryGroupings, { categoryIds } from '~app/pages/Pricing/transformers/details/groupings';
import { PackageDataSet } from '~shared/types/pricing/package';
import { NavRouteParams } from '~pricing/components/Navigation/types';
import { usePricingExperiments } from '~shared/hooks/usePricingExperiments';
import { DetailsFeatureRowFeature } from '~pricing/transformers/details/types';

import FeatureCell from './FeatureCell';
import useCategoryStyles from './useStyles';
import COPY from '~app/pages/Pricing/copy';
import { ToggleState } from './types';
import { usePricingState } from '~shared/hooks/usePricingState';

const CaretDown = <IconCaretDown title="Down Arrow" />;
const CaretUp = <IconCaretUp title="Up Arrow" />;
const expandAll = t(COPY.EXPAND_ALL);
const collapseAll = t(COPY.COLLAPSE_ALL);

export default function Categories({
  displayedPackages,
  isMobile,
  stickyRef,
}: Readonly<{
  displayedPackages: PackageDataSet;
  isMobile?: boolean;
  stickyRef?: RefObject<HTMLDivElement>;
}>): JSX.Element {
  const { canonicalPath, subPath } = useParams<NavRouteParams>();
  const { uiExperiment, priceExperiment } = usePricingExperiments();

  const {
    state: {
      packages: { detailsPackageData, displayPackageSet },
    },
  } = usePricingState();

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const featureId = query.get('selected_feature');
  const highlightedFeature = useRef<HTMLObjectElement>(null);
  const categoryId = query.get('selected_category');
  const selectedCategoryRef = useRef<HTMLObjectElement>(null);
  const [expColButtonPos, setExpColButtonPos] = useState(0);

  // Scroll to the category or highlighted feature if it exists on page load
  useEffect(() => {
    if ((highlightedFeature.current || selectedCategoryRef.current) && !isMobile) {
      setTimeout(() => {
        const offsetTop = highlightedFeature?.current?.offsetTop ?? selectedCategoryRef?.current?.offsetTop ?? 0;
        const packagesHeader = document.getElementById('PackagesHeader');
        const offsetHeight = packagesHeader ? packagesHeader.clientHeight : 0;

        window.scrollTo({ top: offsetTop - offsetHeight, behavior: 'smooth' });
      }, 1000);
    }
  }, [isMobile]);

  const {
    AccordionControl,
    DetailRow,
    Feature,
    HighlightFeature,
    TooltipContainer,
    TooltipIndicator,
    Tooltip,
    ExpandCollapseContainer,
    ExpandCollapse,
  } = useCategoryStyles({
    packageCount: displayedPackages.length,
  });

  function calcTopPos(value: number): number {
    return value - 31;
  }

  useEffect(() => {
    if (stickyRef?.current) {
      setExpColButtonPos(calcTopPos(stickyRef.current.clientHeight));
    }
  }, [stickyRef]);

  useEffect(() => {
    const options = {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    };

    const handleChange = debounce(function handleChange() {
      if (stickyRef?.current) {
        setExpColButtonPos(calcTopPos(stickyRef?.current?.clientHeight));
      }
    }, 100);

    const observer = new MutationObserver(handleChange);
    if (stickyRef?.current) {
      observer.observe(stickyRef.current, options);
    }
    window.addEventListener('resize', handleChange);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleChange);
    };
  });

  // get the experience name if there is one
  const experience =
    (uiExperiment.hasAssignment && uiExperiment.treatmentName) ||
    (priceExperiment.hasAssignment && priceExperiment.treatmentName) ||
    '';
  /**
   * Since /pricing/details is a valid route, let's handle experiments categories accordingly
   * See src/app/pages/Pricing/pages/index.ts L24 for the regex path matching
   */
  const currentPath = canonicalPath ?? subPath;

  const categories = useMemo(() => {
    const groupings = categoryGroupings[currentPath][experience] ?? categoryGroupings[currentPath].default;
    return getFeatureCategories(displayedPackages, groupings, detailsPackageData);
  }, [currentPath, experience, displayedPackages, detailsPackageData]);

  const categoryIndices: number[] = categories.map((_category, index) => index);

  const initialState: ToggleState = useMemo(() => {
    return {
      initialLoad: isMobile,
      isExpanded: !isMobile,
      indices: isMobile ? [0] : categoryIndices,
      text: isMobile ? expandAll : collapseAll,
      icon: isMobile ? CaretUp : CaretDown,
    };
  }, [categoryIndices, isMobile]);
  const [toggleData, setToggleData] = useState(initialState);

  const handleToggle = useCallback(
    (event: React.SyntheticEvent): void => {
      event.preventDefault();
      setToggleData({
        initialLoad: false,
        isExpanded: !toggleData.isExpanded,
        indices: !toggleData.initialLoad && toggleData.indices.length ? [] : categoryIndices,
        text: toggleData.isExpanded ? expandAll : collapseAll,
        icon: toggleData.isExpanded ? CaretUp : CaretDown,
      });
    },
    [categoryIndices, toggleData]
  );

  const key = isMobile ? 'Mobile' : 'Desktop';

  const featureRowCells = (feature: DetailsFeatureRowFeature, id: string): JSX.Element => {
    // if isMobile, map over only the individual package passed in, otherwise map over only the displayed package set.
    const data = isMobile ? [displayedPackages[0].packageName] : displayPackageSet;
    return (
      <>
        {data.map(pkg => (
          <FeatureCell {...{ packageName: pkg, value: feature.packages?.[pkg] }} key={`${key}-${id}-${pkg}`} />
        ))}
      </>
    );
  };

  return (
    <>
      <div className={ExpandCollapseContainer} style={{ top: expColButtonPos }}>
        <Button size="sm" className={ExpandCollapse} onClick={handleToggle}>
          {toggleData.text}
          {toggleData.icon}
        </Button>
      </div>
      <Box className={AccordionControl} data-testid="Categories">
        <Accordion
          multiple
          onChange={(_expandedIndices: React.ReactNode[], index: number, _id: string, isExpanding: boolean) => {
            setToggleData(state => {
              return {
                ...state,
                indices: isExpanding ? [...state.indices, index] : state.indices.filter(item => item !== index),
              };
            });
          }}
        >
          {/* name: NAME rename to preserve the COPY constant format */}
          {categories.map(({ name: NAME, features }, index) => {
            const isSelectedCategory =
              categoryId && categoryIds[categoryId as keyof typeof categoryIds] === NAME.defaultMessage;
            return (
              <Accordion.Item
                key={`${key}-${NAME.id}`}
                id={t(NAME)}
                expanded={
                  !!toggleData.indices.includes(index) ||
                  Boolean(featureId && features.find(feature => feature.name === featureId))
                }
                title={t(NAME)}
                ref={isSelectedCategory ? selectedCategoryRef : null}
              >
                <section role="table" aria-label={NAME.defaultMessage}>
                  {features?.map(feature => {
                    const isHighlightedFeature = featureId && featureId === feature.name;
                    return (
                      <div
                        className={`${DetailRow} ${isHighlightedFeature ? HighlightFeature : ''}`}
                        ref={isHighlightedFeature ? highlightedFeature : null}
                        key={`${key}-${feature.name}`}
                        role="row"
                        tabIndex={-1}
                      >
                        <div className={Feature} role="rowheader" aria-label={feature.summary} tabIndex={-1}>
                          {feature.summary}
                          {feature.tooltip && feature.tooltipHeading && (
                            <span className={TooltipContainer}>
                              <span className={TooltipIndicator}>?</span>
                              <span className={Tooltip}>
                                <FeatureTooltip tooltip={feature.tooltip} tooltipHeading={feature.tooltipHeading} />
                              </span>
                            </span>
                          )}
                        </div>
                        {featureRowCells(feature, NAME.id)}
                      </div>
                    );
                  })}
                </section>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Box>
    </>
  );
}
